import React, { useEffect, useMemo, useRef, useState } from "react";
import AddressInput from "./AddressInput";
import CustomInput from "./CustomInput";
import InputAddress from "./InputAddress";
import InputArray from "./InputArray";
import InputDatalist from "./InputDatalist";
import InputDropDown from "./InputDropDown";
import InputImage from "./InputImage";
import InputMultipleDropDown from "./InputMultipleDropdown";
import InputPhone from "./InputPhone";
import InputValueDropDown from "./InputValueDropDown";
import VaccineInput from "./VaccineInput";
import InputRadio from "./InputRadio";
import InputDropdownwithOthers from "./InputDropdownOthers";
import ModernDateInput from "./ModernDateInput";
import "../../css/register.css";
import { useTranslation } from "react-i18next";

const ModernDropdown = ({
	name,
	label,
	value,
	options = [],
	defaultName,
	required,
	disabled,
	onChange,
	onBlur,
	error,
	searchable,
	multiple = false,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const dropdownRef = useRef(null);
	const pendingValues = useRef([]);
	const selectionChanged = useRef(false);
	const placeholder = defaultName || "Select An Option";
	const selectedValues = multiple && Array.isArray(value) ? value.map(String) : [];
	const selectedValue = multiple ? "" : value || "";
	const normalizedSearch = searchText.trim().toLowerCase();
	const isSearchable = searchable || options.length > 12;
	const normalizedOptions = useMemo(
		() =>
			options.map((option) =>
				typeof option === "object" && option !== null
					? {
							label: option.label || option.name || option.value || "",
							value: option.value || option.id || option._id || "",
						}
					: { label: option, value: option }
			),
		[options]
	);
	const selectedOption = normalizedOptions.find((option) => option.value === selectedValue);
	const selectedLabels = normalizedOptions.filter((option) => selectedValues.includes(String(option.value))).map((option) => t(option.label));
	useEffect(() => { if (multiple) pendingValues.current = selectedValues; }, [value, multiple]);
	const closeDropdown = () => {
		setIsOpen(false);
		setSearchText("");
		if (multiple && selectionChanged.current) {
			selectionChanged.current = false;
			onBlur?.({ target: { id: name, name, type: "multipleDropdown", value: pendingValues.current } });
		}
	};
	const visibleOptions = useMemo(() => {
		if (!isSearchable) return normalizedOptions;
		if (!normalizedSearch) return normalizedOptions;

		return normalizedOptions
			.map((option) => {
				const labelText = t(option.label || "").toLowerCase();
				const matchIndex = labelText.indexOf(normalizedSearch);
				const startsWithSearch = labelText.startsWith(normalizedSearch);
				const wordStartsWithSearch = labelText
					.split(/[\s,-]+/)
					.some((word) => word.startsWith(normalizedSearch));

				let score = Number.POSITIVE_INFINITY;
				if (labelText === normalizedSearch) score = 0;
				else if (startsWithSearch) score = 1;
				else if (wordStartsWithSearch) score = 2;
				else if (matchIndex >= 0) score = 3 + matchIndex / 100;

				return {
					option,
					labelText,
					score,
				};
			})
			.filter(({ score }) => Number.isFinite(score))
			.sort((first, second) => {
				if (first.score !== second.score) return first.score - second.score;
				if (first.score <= 1 && first.labelText.length !== second.labelText.length) {
					return first.labelText.length - second.labelText.length;
				}
				return first.labelText.localeCompare(second.labelText);
			})
			.map(({ option }) => option);
	}, [isSearchable, normalizedOptions, normalizedSearch, t]);

	useEffect(() => {
		if (!isOpen) return undefined;

		const handleOutsideClick = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				closeDropdown();
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [isOpen, multiple, onBlur]);
	const changeMultiple = (nextValues) => {
		pendingValues.current = nextValues;
		selectionChanged.current = true;
		onChange({ target: { id: name, name, type: "multipleDropdown", value: nextValues } });
	};

	const commitValue = (nextValue) => {
		if (multiple) {
			const current = pendingValues.current;
			changeMultiple(current.includes(String(nextValue))
				? current.filter((item) => item !== String(nextValue))
				: [...current, String(nextValue)]);
			return;
		}
		const event = {
			target: {
				id: name,
				name,
				type: "select-one",
				value: nextValue,
				required,
				validity: {
					valueMissing: required && !nextValue,
					typeMismatch: false,
				},
			},
		};
		onChange(event);
		if (onBlur) {
			setTimeout(() => onBlur(event), 0);
		}
		closeDropdown();
	};

	return (
		<div className="staff-modern-select-field" ref={dropdownRef}>
			{label ? (
				<label htmlFor={name}>
					{t(label)} {required ? <span style={{ color: "red" }}>*</span> : null}
				</label>
			) : null}
			<button
				type="button"
				id={name}
				className={`staff-modern-select-trigger${multiple ? selectedValues.length ? "" : " is-empty" : selectedValue ? "" : " is-empty"}`}
				disabled={disabled}
				onClick={() => isOpen ? closeDropdown() : setIsOpen(true)}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<span>{multiple ? selectedLabels.join(", ") || t(placeholder) : selectedOption ? t(selectedOption.label) : t(placeholder)}</span>
				<span className="material-icons">expand_more</span>
			</button>
			{isOpen ? (
				<div className="staff-modern-select-menu" role="listbox" aria-multiselectable={multiple || undefined}>
					{isSearchable ? (
						<input
							type="search"
							className="staff-modern-select-search"
							value={searchText}
							onChange={(event) => setSearchText(event.target.value)}
							placeholder={t("Search")}
							onClick={(event) => event.stopPropagation()}
							autoFocus
						/>
					) : null}
					{multiple && selectedValues.length ? <button type="button" className="staff-modern-select-option" onClick={() => changeMultiple([])}>{t("Clear selection")}</button> : null}
					{visibleOptions.map((option) => {
						const isSelected = multiple ? selectedValues.includes(String(option.value)) : option.value === selectedValue;
						return (
							<button
								type="button"
								key={option.value || option.label}
								className={`staff-modern-select-option${
									isSelected ? " is-selected" : ""
								}`}
								onClick={() => commitValue(option.value)}
								role="option"
								aria-selected={isSelected}
							>
								<span>{t(option.label)}</span>
								{isSelected ? <span className="material-icons">check</span> : null}
							</button>
						);
					})}
					{visibleOptions.length === 0 ? (
						<div className="staff-modern-select-empty">{t("No options found")}</div>
					) : null}
				</div>
			) : null}
			{error ? <p className="red-text m-0">{t("Required field.")}</p> : null}
		</div>
	);
};

const InputRenderer = ({
	handleChange,
	values,
	data,
	handleArrayChange,
	handleBlur,
	errors,
	isEnrollment,
	isChildProfile,
	disabled,
	...props
}) => {
	let emaildupCheck = 0;
	let mobiledupCheck = 0;
	const getInitials = (...names) =>
		names
			.map((name) => name?.trim()?.[0] || "")
			.join("")
			.toUpperCase() || "?";

	if (values.motherEmail === values.fatherEmail && values.fatherEmail) {
		emaildupCheck = 1;
	} else {
		emaildupCheck = 0;
	}

	if (values.motherMobile === values.fatherMobile && values.fatherMobile) {
		mobiledupCheck = 1;
	} else {
		mobiledupCheck = 0;
	}

	return data.map((ele) => {
		if (Array.isArray(ele.initialValue) && ele.type !== "multipleDropdown") {
			return (
				<InputArray
					name={ele.name}
					label={ele.label}
					disabled={isEnrollment || disabled}
					value={values[ele.name]}
					onChange={handleArrayChange}
					onBlur={handleBlur}
				/>
			);
		}
		if (Array.isArray(ele)) {
			if (ele[0].type === "vaccine") {
				return (
					<VaccineInput
						values={values}
						ele={ele}
						disabled={isEnrollment || disabled}
						handleBlur={handleBlur}
						handleChange={handleArrayChange}
						modernDates={props.modernDates || props.prospects}
					/>
				);
			} else if (ele[0].type === "dropdown") {
				return (
					<InputDropdownwithOthers
						values={values}
						ele={ele}
						isEnrollment={isEnrollment}
						handleBlur={handleBlur}
						handleChange={handleChange}
						errors={errors}
						disabled={disabled}
					/>
				);
			} else if (ele[0].type === "string") {
				return (
					<div className="d-flex align-items-center justify-content-between">
						<CustomInput
							key={ele[0].name}
							label={ele[0].label}
							type={ele[0].type}
							name={ele[0].name}
							required={ele[0].required}
							value={values[ele[0].name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
						/>
						<CustomInput
							key={ele[1].name}
							label={ele[1].label}
							type={ele[1].type}
							name={ele[1].name}
							required={ele[1].required}
							value={values[ele[1].name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
						/>
					</div>
				);
			} else {
				return (
					<div className="d-flex align-items-center">
						<InputValueDropDown
							value={[values[ele[0].name], values[ele[1].name]]}
							name={[ele[0].name, ele[1].name]}
							label={[ele[0].label]}
							onBlur={handleBlur}
							onChange={handleChange}
							required={ele[0].required}
							type={ele[0].type}
							disabled={isEnrollment || disabled}
							options={ele[1].options}
							error={errors[ele[0].name]}
						/>
					</div>
				);
			}
		} else {
			switch (ele.type) {
				case "tel":
					return (
						<div className="form-group child-register-phone-wrapper">
							<InputPhone
								{...ele}
								onChange={handleChange}
								value={values[ele.name]}
								data={values}
								onBlur={handleBlur}
								error={errors[ele.name]}
								disabled={isEnrollment || disabled}
								duplicateMobileError={mobiledupCheck}
							/>
						</div>
					);
				case "file": {
					const initials =
						ele.name === "photo"
							? getInitials(values.firstName, values.lastName)
							: ele.name === "motherPhoto"
								? getInitials(values.motherName, values.motherLastName)
								: ele.name === "fatherPhoto"
									? getInitials(values.fatherName, values.fatherLastName)
									: "?";

					return (
						<InputImage
							{...ele}
							onChange={handleChange}
							value={values[ele.name]}
							initials={initials}
							disabled={isEnrollment || disabled}
							style={{
								gridRowStart: "1",
								gridRowEnd: `${Math.floor(data.length - 1) + 1}`,
							}}
						/>
					);
				}

				case "dropdown":
					return props.modernFields ? (
						<ModernDropdown
							value={values[ele.name]}
							label={ele.label}
							name={ele.name}
							options={ele.options}
							onChange={handleChange}
							onBlur={handleBlur}
							defaultName={ele.defaultName}
							required={ele?.required}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
							searchable={ele.searchable}
						/>
					) : (
						<InputDropDown
							prospects={props.prospects}
							value={values[ele.name]}
							label={ele.label}
							name={ele.name}
							options={ele.options}
							onChange={handleChange}
							onBlur={handleBlur}
							defaultName={ele.defaultName}
							required={ele?.required}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
						/>
					);
				case "multipleDropdown":
					return props.modernFields ? (
						<ModernDropdown
							value={values[ele.name] || []}
							label={ele.label}
							name={ele.name}
							options={ele.options}
							onChange={handleChange}
							onBlur={handleBlur}
							defaultName={ele.defaultName}
							disabled={isEnrollment || disabled}
							searchable={ele.searchable}
							multiple
						/>
					) : (
						<InputMultipleDropDown
							options={ele.options}
							label={ele.label}
							name={ele.name}
							value={values[ele.name] || []}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
						/>
					);
				case "datalist":
					return (
						<InputDatalist
							name={ele.name}
							label={ele.label}
							type={ele.type}
							start={ele.start}
							end={ele.end}
							step={ele.step}
							required={ele.required}
							value={values[ele.name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
							wrapperStyle={{ marginBottom: "30px" }}
						/>
					);
				case "textarea":
					return isEnrollment ? (
						<div>
							<p className="m-0">Comment</p>
							<textarea
								style={{
									width: "100%",
									border: "rgb(206, 212, 218) solid 1px",
									borderRadius: "5px",
								}}
								value={values[ele.name]}
								id={ele.name}
								onChange={handleChange}
								onBlur={handleBlur}
								disabled={disabled}
							/>
						</div>
					) : null;
				case "address":
					return (
						<InputAddress
							value={values[ele.name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
							required={ele.required}
							label={ele.label}
							name={ele.name}
						/>
					);
				case "address1":
					return (
						<AddressInput
							key={ele.name}
							label={ele.label}
							type={ele.type}
							name={ele.name}
							required={ele.required}
							value={values[ele.name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
							min={ele.min}
						/>
					);
				case "date":
					return props.prospects || props.modernDates ? (
						<ModernDateInput
							key={ele.name}
							label={ele.label}
							name={ele.name}
							required={ele.required}
							value={values[ele.name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
						/>
					) : (
						<CustomInput
							association={ele.association || ""}
							key={ele.name}
							label={ele.label}
							type={ele.type}
							name={ele.name}
							required={ele.required}
							value={values[ele.name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
							min={ele.min}
							duplicateEmailError={emaildupCheck}
						/>
					);
				case "radio":
					return (
						<InputRadio
							value={values[ele.name]}
							label={ele.label}
							name={ele.name}
							options={ele.options}
							onChange={handleChange}
							onBlur={handleBlur}
							defaultName={ele.defaultName}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
						/>
					);
				default:
					return (
						<CustomInput
							association={ele.association || ""}
							key={ele.name}
							label={ele.label}
							type={ele.type}
							name={ele.name}
							required={ele.required}
							value={values[ele.name]}
							onChange={handleChange}
							onBlur={handleBlur}
							disabled={isEnrollment || disabled}
							error={errors[ele.name]}
							min={ele.min}
							duplicateEmailError={emaildupCheck}
						/>
					);
			}
		}
	});
};

export default InputRenderer;
